import styled from '@emotion/styled'
import { useQueryClient } from '@tanstack/react-query'
import { Modal, Input, Button, Form, notification } from 'antd'
import type { RuleObject } from 'antd/lib/form'
import type { StoreValue } from 'antd/lib/form/interface'
import BigNumber from 'bignumber.js'
import numeral from 'numeral'
import { useMemo, useState } from 'react'
import { parseEther } from 'viem'
import { useAccount, useBalance, useChainId, useWriteContract } from 'wagmi'

import error_icon from '@/assets/error_icon.png'
import success_icon from '@/assets/success_icon.png'
import { config } from '@/config'
import { useLocalStorage } from '@/context/localstorage-context'
import type { PosStaker } from '@/types/pos-staker'
import { checkAmountUnit, formatBalance, getRewardTime } from '@/utils'

import { Link } from '../pos-detail.module'

import TopupConfirm from './topup-confirm'

export default function TopupModal({
  type,
  visible,
  onCancel,
  stakerInfo,
}: {
  type: string
  visible: boolean
  onCancel: () => void
  stakerInfo: PosStaker
}) {
  const chainId = useChainId()
  const [showConfirm, setShowConfirm] = useState(false)
  const { address } = useAccount()
  const { data, status } = useBalance({ address })
  const queryClient = useQueryClient()

  const [form] = Form.useForm()
  const { theme } = useLocalStorage()
  const isdark = useMemo(() => theme === 'dark', [theme])
  const { data: hash, writeContractAsync } = useWriteContract()
  const PosContractConfig = config[chainId]['PosContract']

  const balance = useMemo(() => {
    return data && status === 'success'
      ? formatBalance(data.value.toString(10))
      : '0'
  }, [data, status])

  const checkAmount = async (rule: RuleObject, value: StoreValue) => {
    try {
      if (!checkAmountUnit(value) || new BigNumber(value).lte(0)) {
        return Promise.reject(new Error('Please enter correct amount!'))
      }
      if (new BigNumber(value).gte(balance)) {
        return Promise.reject(new Error('Please enter correct amount!'))
      }
      return Promise.resolve()
    } catch {
      return Promise.reject(new Error('Please enter correct amount!'))
    }
  }

  const onOk = () => {
    form
      .validateFields()
      .then(() => {
        setShowConfirm(true)
      })
      .catch((error) => {
        console.log(error, 'onOk')
      })
  }

  const posStakeAppend = async (amount: string) => {
    try {
      const hash = await writeContractAsync({
        address: PosContractConfig.addr,
        abi: PosContractConfig.abi,
        functionName: 'stakeAppend',
        args: [stakerInfo.address],
        value: parseEther(amount),
      })
      notification.success({
        message: 'Transaction completed',
        description: <Link txHash={hash} />,
        className: `${isdark ? 'noticeSuccess_dark' : 'noticeSuccess_light'}`,
        icon: <img width={30} src={success_icon} alt="notification" />,
      })
      queryClient.invalidateQueries({
        queryKey: [
          'getValidatorStakeInfo',
          {
            address,
          },
          !!address,
        ],
      })
      setShowConfirm(false)
    } catch (error) {
      console.error('posStakeAppend', error)
      notification.error({
        message: 'Transaction Sent Failed',
        description: <Link txHash={hash || ''} />,
        className: `${isdark ? 'noticeSuccess_dark' : 'noticeSuccess_light'}`,
        icon: <img width={30} src={error_icon} alt="notification" />,
      })
      setShowConfirm(false)
    }
  }

  return (
    <Container>
      <Modal
        className={isdark ? 'darkModal' : 'lightModal'}
        maskClosable={true}
        closable={false}
        destroyOnClose={true}
        style={{ marginTop: '2%', borderRadius: '16px' }}
        width={700}
        mask={true}
        open={visible}
        title={
          <Title className="text-light-0 dark:text-white">
            {type} WAN with {stakerInfo.name}
          </Title>
        }
        footer={null}
        onCancel={onCancel}
      >
        <Form form={form} name="delegate">
          <Form.Item
            name="amount"
            rules={[{ required: true, validator: checkAmount }]}
          >
            <Input />
          </Form.Item>
        </Form>
        <AddrInfo>
          <span>Balance</span>
          <span>
            <Data>
              {numeral(balance)
                .format('0,0.00000')
                .replace(/\.?0+$/, '')}
            </Data>{' '}
            WAN
          </span>
        </AddrInfo>
        <AddrInfo>
          <span>Stake from</span>
          <AddrText>{address}</AddrText>
        </AddrInfo>
        <BtnArea isdark={isdark}>
          <Button onClick={onCancel}>Cancel</Button>
          <Button onClick={onOk}>OK</Button>
        </BtnArea>
        <Info isdark={isdark}>
          <div>Notice</div>
          <ul style={{ listStyleType: 'disc' }}>
            <li>
              The top-up WAN will start to accrue rewards on UTC{' '}
              {getRewardTime()}.
            </li>
          </ul>
        </Info>
      </Modal>
      {showConfirm && (
        <TopupConfirm
          visible={showConfirm}
          onCancel={() => setShowConfirm(false)}
          stakerInfo={stakerInfo}
          action={posStakeAppend}
          amount={form.getFieldValue('amount')}
        />
      )}
    </Container>
  )
}

const Container = styled.div`
  .ant-input {
    border: 1px solid #2fbdf4;
  }
`

const Title = styled.span`
  font-size: 24px;
  font-family: Inter-Bold, Inter;
  font-weight: 400;
`

const AddrInfo = styled.div`
  display: flex;
  color: #757575;
  font-size: 14px;
  justify-content: space-between;

  span {
    color: #818d96;
  }
`

const Data = styled.span`
  color: #2fbdf4 !important;
`

const BtnArea = styled.div<{
  isdark: boolean
}>`
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
  gap: 20px;

  .ant-btn:first-of-type {
    background: ${(props) => (props.isdark ? '#1E3546' : '#E5E5E5')};
    border-radius: 16px;
    color: ${(props) => (props.isdark ? '#818D96' : '##999999')};
  }
  .ant-btn:first-of-type:hover {
    background: ${(props) => (props.isdark ? '#1E3546' : '#E5E5E5')};
  }
  .ant-btn {
    width: calc((100% - 20px) / 2);
    border-radius: 16px;
    height: 60px;
    color: white;
    border: none;
    background: #0f68aa;
  }
  .ant-btn:hover {
    background: #0f68aa;
  }
`

const Info = styled.div<{
  isdark: boolean
}>`
  font-size: 14px;
  font-family: Inter-Regular, Inter;
  font-weight: 400;
  color: ${(props) => (props.isdark ? '#818d96' : '#999')};
  margin-top: 20px;
  border-radius: 16px;
  border: ${(props) =>
    props.isdark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #F5F5F5'};
  padding: 15px;
  text-align: left;
  background-color: ${(props) => (props.isdark ? '#051F31' : '#F5F5F5')};

  ul {
    padding-left: 1rem;
  }
`

const AddrText = styled.span`
  @media (max-width: 768px) {
    width: 60%;
    text-align: right;
  }
`
